import { Link } from 'react-router-dom';

export default function About() {
  const approachPoints = [
    "Experience-led design: We architect complete travel experiences from concept to execution",
    "Scalable formats: IP-driven models that grow across markets without dilution",
    "Partnerships: Certified operator network ensuring local expertise and compliance",
    "Governance: Structured frameworks maintaining quality, consistency, and brand integrity"
  ];

  const whatWeDontDo = [
    "No ticket sales: We don't sell individual travel tickets or packages",
    "No tour operations: We don't operate tours directly",
    "No competition with agencies: We partner with travel operators, not compete with them",
    "No consumer retail: We don't target individual travelers",
    "No commoditized products: We create unique, IP-protected experiences"
  ];

  const experiences = [
    {
      title: "The Great West Africa Rendezvous (GWR)",
      description: "A large-scale cultural and destination experience designed to bring West Africa together through a neutral meeting point.",
      points: [
        "Designed & owned by: MGL Experiences Ltd (UK)",
        "Executed by: Regional travel operators",
        "Focus: Culture, connection, scale, and legacy",
        "Format: Annual cultural convergence across West African destinations"
      ],
      image: "/assets/hero/cultural.jpeg",
      color: "from-amber-500 to-orange-600"
    },
    {
      title: "Boundless Europe Experience",
      description: "A multi-country European travel format designed for group travel, diaspora engagement, and cultural immersion.",
      points: [
        "Multi-country European travel format",
        "Diaspora & group-focused experience",
        "Licensed to approved travel operators",
        "Cross-cultural exchange and heritage exploration"
      ],
      image: "/assets/hero/europe.jpeg",
      color: "from-blue-500 to-indigo-600"
    }
  ];

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: "url('/assets/hero/cultural.jpeg')" }}
          ></div>
        </div>
        <div className="section-container relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Who We Are</h1>
            <p className="text-xl md:text-2xl text-gray-200 leading-relaxed">
              MGL Experiences Ltd is a UK-based travel events and experiences company focused on designing, owning, and scaling destination-led travel experiences and intellectual property.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="section-container">
        {/* Full Description */}
        <div className="max-w-4xl mx-auto mb-16">
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            We create large-scale cultural and lifestyle experiences delivered through trusted regional travel operators across Africa, Europe, and beyond. Our model transforms destinations into scalable experience formats while maintaining quality, consistency, and compliance through strategic partnerships.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Our Approach */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Approach</h2>
              <ul className="space-y-4">
                {approachPoints.map((point, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* What We Don't Do */}
            <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-red-500">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">What We Don't Do</h2>
              <ul className="space-y-4">
                {whatWeDontDo.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Experiences Showcase */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Experience Formats</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Presenting travel experiences as licensable intellectual property, not retail products.
            </p>
          </div>

          <div className="space-y-12">
            {experiences.map((exp, index) => (
              <div key={index} className={`grid lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Image Section */}
                <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-2xl group">
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                    style={{ backgroundImage: `url(${exp.image})` }}
                  ></div>
                  <div className={`absolute inset-0 bg-gradient-to-t ${exp.color} opacity-30`}></div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                    <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium">
                      {exp.title.includes('West Africa') ? 'Cultural IP' : 'European IP'}
                    </span>
                  </div>
                </div>

                {/* Content Section */}
                <div>
                  <div className="inline-flex items-center mb-4">
                    <div className={`w-4 h-4 bg-gradient-to-r ${exp.color} rounded-full mr-2`}></div>
                    <span className="text-sm font-medium text-primary uppercase tracking-wider">
                      Licensable IP Format
                    </span>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{exp.title}</h3>
                  <p className="text-gray-600 mb-6">{exp.description}</p>
                  
                  <ul className="space-y-3 mb-8">
                    {exp.points.map((point, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-5 h-5 text-primary mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link 
                    to="/experiences" 
                    className="inline-flex items-center text-primary font-medium hover:text-primary-dark"
                  >
                    Learn more about this IP format
                    <svg className="w-5 h-5 ml-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Company Philosophy */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Positioning Statement</h2>
            <div className="w-16 h-1 bg-primary mx-auto"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                </svg>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Experience Architect</h3>
              <p className="text-sm text-gray-600">We design complete destination experiences</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">IP Owner</h3>
              <p className="text-sm text-gray-600">We own and protect the experience formats</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Partnership Leader</h3>
              <p className="text-sm text-gray-600">We enable execution through certified operators</p>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-200 text-center">
            <p className="text-gray-600 italic">
              "We don't sell trips. We design experiences. We don't operate tours. We own IP. We don't compete with agencies. We partner with them."
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}